'use client';

import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import { InView } from '@/components/motion/InView';
import type { RestaurantLocale } from '@restaurant/config';
import styles from './DailySpecialSection.module.css';

export interface HomeDailySpecial {
  slug: string;
  type: 'MENU_DU_JOUR' | 'PLAT_DU_JOUR';
  title: { fr: string; en: string; es: string };
  excerpt: { fr: string; en: string; es: string };
  content: { fr: string; en: string; es: string };
  price: number | null;
  imageUrl: string | null;
  imageAlt: { fr: string; en: string; es: string };
  isTakeaway: boolean;
  hasSinglePage: boolean;
}

interface DailySpecialSectionProps {
  special: HomeDailySpecial;
  locale: RestaurantLocale;
}

const LABELS = {
  fr: {
    eyebrow: 'Suggestion du chef',
    menuBadge: 'Menu du jour',
    platBadge: 'Plat du jour',
    takeaway: 'Disponible à emporter',
    cta: 'Voir la carte du jour',
  },
  en: {
    eyebrow: "Chef's special",
    menuBadge: 'Menu of the day',
    platBadge: 'Dish of the day',
    takeaway: 'Available for takeaway',
    cta: "See today's menu",
  },
  es: {
    eyebrow: 'Sugerencia del chef',
    menuBadge: 'Menú del día',
    platBadge: 'Plato del día',
    takeaway: 'Disponible para llevar',
    cta: 'Ver el menú del día',
  },
} as const;

export function DailySpecialSection({ special, locale }: DailySpecialSectionProps) {
  const lk = locale === 'en' || locale === 'es' ? locale : 'fr';
  const l = LABELS[lk];
  const title = special.title[lk] || special.title.fr;
  const excerpt = special.excerpt[lk] || special.excerpt.fr;
  const content = special.content[lk] || special.content.fr;
  const alt = special.imageAlt[lk] || special.imageAlt.fr || title;
  const badge = special.type === 'MENU_DU_JOUR' ? l.menuBadge : l.platBadge;
  const price =
    special.price !== null
      ? new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
        }).format(special.price)
      : null;

  const cardContent = (
    <div className={styles.card}>
      {special.imageUrl ? (
        <div className={styles.imageWrap}>
          <Image
            src={special.imageUrl}
            alt={alt}
            className={styles.image}
            fill
            quality={70}
            sizes="(max-width: 680px) 100vw, 38vw"
          />
        </div>
      ) : (
        <div className={styles.imagePlaceholder} />
      )}
      <div className={styles.body}>
        <p className={styles.badge}>{badge}</p>
        <h3 className={styles.cardTitle}>{title}</h3>
        {excerpt && <p className={styles.excerpt}>{excerpt}</p>}
        {content && <p className={styles.content}>{content}</p>}
        <div className={styles.footer}>
          {price && <span className={styles.price}>{price}</span>}
          {special.isTakeaway && (
            <span className={styles.takeawayTag}>{l.takeaway}</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <InView className={styles.header} y={20} delay={0}>
          <span className={styles.eyebrow}>{l.eyebrow}</span>
        </InView>

        <InView y={28} delay={0.1}>
          {special.hasSinglePage ? (
            <Link
              href={{
                pathname: '/en-ce-moment/plat-du-jour/[slug]',
                params: { slug: special.slug },
              }}
              className={styles.cardLink}
            >
              {cardContent}
            </Link>
          ) : (
            cardContent
          )}
        </InView>

        <InView className={styles.ctaRow} y={16} delay={0.2}>
          <Link
            href={{ pathname: '/en-ce-moment/plat-du-jour' }}
            className={styles.cta}
          >
            {l.cta} →
          </Link>
        </InView>
      </div>
    </section>
  );
}
